import { ref } from 'vue'
import { Browser } from '@capacitor/browser'
import { loadConfig } from '@/configLoader'
import { Device } from '@capacitor/device'
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin'
import { Preferences } from '@capacitor/preferences'
import Swal from '@/services/ui/dialog'
import { Capacitor } from '@capacitor/core'

import { parseJwt, isTokenExpired } from './utils'

const token = ref(null)
const isWebPlatform = platform => platform === 'web'

async function getStorage() {
  const { platform } = await Device.getInfo()
  return platform === 'web' ? window.localStorage : SecureStoragePlugin
}

export async function getToken() {
  const platform = (await Device.getInfo()).platform
  const key = 'pinestack.token'

  if (isWebPlatform(platform)) {
    return window.localStorage.getItem(key)
  } else {
    const { value } = await Preferences.get({ key })
    return value
  }
}

export async function isIos() {
  const { operatingSystem } = await Device.getInfo()
  return operatingSystem === 'ios'
}
export async function isAndroid() {
  const { operatingSystem } = await Device.getInfo()
  return operatingSystem === 'android'
}

async function setRedirectUrl(url) {
  const { platform } = await Device.getInfo()
  const key = 'redirectAfterLogin'

  if (isWebPlatform(platform)) {
    window.localStorage.setItem(key, url)
  } else {
    await Preferences.set({ key, value: url })
  }
}

async function setToken(tokenValue) {
  const platform = (await Device.getInfo()).platform
  const key = 'pinestack.token'

  if (isWebPlatform(platform)) {
    window.localStorage.setItem(key, tokenValue)
  } else {
    await Preferences.set({ key, value: tokenValue })
  }

  token.value = tokenValue
}

async function removeToken() {
  const keys = [
    'pinestack.token',
    'token',
    'access_token',
    'refreshToken',
    'refresh_token',
    'redirectAfterLogin'
  ]
  try {
    for (const k of keys) {
      window.localStorage.removeItem(k)
      window.sessionStorage.removeItem(k)
      await Preferences.remove({ key: k }).catch(() => {})
    }
  } catch (e) {
    console.warn('[OAuth] removeToken warning:', e)
  }
  token.value = null
}

async function getRedirectUrl() {
  const { platform } = await Device.getInfo()
  const key = 'redirectAfterLogin'
  if (isWebPlatform(platform)) {
    return window.localStorage.getItem(key)
  } else {
    const { value } = await Preferences.get({ key })
    return value
  }
}

export async function getInstalledAppName() {
  if (!Capacitor.isNativePlatform()) return null

  try {
    const { App } = await import('@capacitor/app')
    const info = await App.getInfo()
    return info?.name || null
  } catch (error) {
    console.error('Failed to get app info:', error)
    return null
  }
}

export async function appInstalledNative() {
  if (!Capacitor.isNativePlatform()) return false

  try {
    const { App } = await import('@capacitor/app')
    const info = await App.getInfo()
    return !!info?.name
  } catch (error) {
    console.error('Failed to check if app is installed:', error)
    return false
  }
}

async function authenticate() {
  try {
    const config = await loadConfig()
    const { platform } = await Device.getInfo()
    let isIOS = await isIos()
    // Store the intended redirect URL before authentication
    const redirectAfterLogin = (await getRedirectUrl()) || '/'
    await setRedirectUrl(redirectAfterLogin)
    let callbackUri = `${window.location.origin}/oauth2/code/callback`
    const hostname = window.location.hostname.toLowerCase()

    let isAppInstalled = await appInstalledNative()
    if (isAppInstalled) {
      callbackUri = hostname === 'localhost' ? config.localAppCallBackUri : config.appCallBackUri
    } else {
      callbackUri = hostname === 'localhost' ? config.localCallbackUri : config.callbackUri
    }
    const lowerHost = hostname?.toLowerCase()
    console.log('isAppInstalled:', isAppInstalled)
    if (!isAppInstalled && (lowerHost.includes('hbc.qa') || lowerHost.includes('pia.qa'))) {
      callbackUri = config?.['callBackUriQA'] || config?.['callbackUri']
    } else if (!isAppInstalled && lowerHost.includes('pia.staging')) {
      callbackUri = config?.['callbackUriStaging'] || config?.['callbackUri']
    }

    const authUrl = `${config.authorizationUri}?client_id=${encodeURIComponent(
      config.clientId
    )}&redirect_uri=${callbackUri}&response_type=token&scope=email profile`

    if (isWebPlatform(platform) && !isIOS) {
      window.open(authUrl, '_self')
    } else if (isIOS) {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
      const isInChrome = /CriOS/.test(navigator.userAgent)

      if (isInChrome && !isStandalone) {
        // Running in Chrome browser on iOS (not as installed app)
        window.open(authUrl, '_self')
      } else {
        // Likely running as standalone iOS app or in Safari/PWA
        const { InAppBrowser } = await import('@capgo/capacitor-inappbrowser')
        const options = {
          url: authUrl,
          showArrow: false,
          title: 'Login',
        }
        console.log('authUrl', authUrl)
        // Browser.open({ url: authUrl, windowName: '_blank' })

        await InAppBrowser.openWebView(options)
        setInterval(() => {
          InAppBrowser.executeScript({
            code: `
      try {
        const url = window.location.href;

        //  Hide 'member-to-be' div immediately (before visible render)
        const style = document.createElement('style');
        style.innerHTML = '.member-to-be { display: none !important; }';
        document.head.appendChild(style);

        // Auto-close WebView on successful login
        if (url === 'https://members.factory.network/dashboard') {
          window.mobileApp?.close?.();
        }

      } catch (e) {
        console.log('Injected script error:', e);
      }
    `,
          })
        }, 10)

        InAppBrowser.addListener('urlChangeEvent', async info => {
          const url = info.url

          if (url.includes('access_token')) {
            try {
              InAppBrowser.close()
              const tokenValue = url.split('access_token=')[1].split('&')[0]
              await setToken(tokenValue)
              const redirectAfterLogin = (await getRedirectUrl()) || '/'
              await setRedirectUrl('')
              window.location.href = redirectAfterLogin
            } catch (e) {
              console.error('Token handling failed:', e)
            }
            return
          }

          if (url === 'https://members.factory.network/dashboard' || url.includes('operations.hammerbrooklyn.pinestack.eu/4a289bb3')) {
            InAppBrowser.close()
          }
        })
      }
    } else {
      const { InAppBrowser } = await import('@capgo/capacitor-inappbrowser')
      const options = {
        url: authUrl,
        closeButtonColor: '#ffffff',
        title: 'Login',
        activeNativeNavigationForWebview: true,
        disableGoBackOnNativeApplication: false,
        isPresentAfterPageLoad: false,
        isAnimated: false,
        showReloadButton: false,
        visibleTitle: false,
        toolbarColor: 'white',
        showArrow: false,
      }

      await InAppBrowser.openWebView(options)
      InAppBrowser.addListener('urlChangeEvent', async info => {
        if (
          info.url.includes('access_token') ||
          info.url === 'https://members.factory.network/dashboard' ||
          info.url?.includes('operations.hammerbrooklyn.pinestack.eu/4a289bb3')
        ) {
          InAppBrowser.close()
          const tokenValue = info.url.split('access_token=')[1].split('&')[0]
          await setToken(tokenValue)
          const redirectAfterLogin = (await getRedirectUrl()) || '/'
          await setRedirectUrl('') // Clear the redirect URL
          window.location.href = redirectAfterLogin
        }
      })
    }
  } catch (error) {
    console.error(error)
  }
}
async function getUserProfile() {
  let access_token = await getToken()
  const userProfile = access_token ? parseJwt(access_token) : null
  return userProfile
}

async function performLogout() {
  await removeToken()
}

export function useOAuthService() {
  return {
    authenticate,
    getToken,
    getStorage,
    setToken,
    removeToken,
    isIos,
    performLogout,
    isLoggedIn: async () => {
      const currentToken = await getToken()
      if (!currentToken) return false
      try {
        const parsed = parseJwt(currentToken)
        if (!parsed || isTokenExpired(parsed)) {
          await removeToken()
          return false
        }
        return true
      } catch (e) {
        return false
      }
    },
    logOut: async () => {
      await removeToken()
    },
    getUserProfile,
  }
}
